import { createApp } from 'vue';
import { createPinia } from 'pinia';
import PrimeVue from 'primevue/config';
import App from './App.vue';
import router from './router';
import './registerServiceWorker';
import { initOfflineSupport } from './services/offline';
import { i18n } from './i18n';

// Disable Vue production hydration mismatch warnings
// Define interface extensions for global window properties
declare global {
  interface Window {
    __VUE_PROD_DEVTOOLS__: boolean;
    __VUE_PROD_HYDRATION_MISMATCH_DETAILS__: boolean;
  }
}

window.__VUE_PROD_DEVTOOLS__ = false;
window.__VUE_PROD_HYDRATION_MISMATCH_DETAILS__ = false;

// PrimeVue styles
import 'primevue/resources/themes/lara-light-indigo/theme.css';
import 'primevue/resources/primevue.min.css';
import 'primeicons/primeicons.css';

// PrimeVue components
import Button from 'primevue/button';
import InputText from 'primevue/inputtext';
import Password from 'primevue/password';
import Checkbox from 'primevue/checkbox';
import Card from 'primevue/card';
import Skeleton from 'primevue/skeleton';
import Avatar from 'primevue/avatar';
import AvatarGroup from 'primevue/avatargroup';
import Badge from 'primevue/badge';
import Tooltip from 'primevue/tooltip';
import ProgressBar from 'primevue/progressbar';
import Chart from 'primevue/chart';
import DataTable from 'primevue/datatable';
import Column from 'primevue/column';
import Tag from 'primevue/tag';
import Dropdown from 'primevue/dropdown';
import ProgressSpinner from 'primevue/progressspinner';
import Toast from 'primevue/toast';
import ToastService from 'primevue/toastservice';
import ConfirmDialog from 'primevue/confirmdialog';
import ConfirmationService from 'primevue/confirmationservice';
import DataView from 'primevue/dataview';
import MultiSelect from 'primevue/multiselect';
import ToggleButton from 'primevue/togglebutton';
import Paginator from 'primevue/paginator';
import Dialog from 'primevue/dialog';
import TabView from 'primevue/tabview';
import TabPanel from 'primevue/tabpanel';
import Textarea from 'primevue/textarea';
import Calendar from 'primevue/calendar';

// Global styles
import './assets/styles/main.scss';
import './styles/index.scss';

// Initialize offline support
initOfflineSupport();

// Register global event listeners for online/offline status
window.addEventListener('online', () => {
  console.log('Application is online');
  // Notify components that we're back online
  window.dispatchEvent(new CustomEvent('app-online'));
});

window.addEventListener('offline', () => {
  console.log('Application is offline');
  // Notify components that we're offline
  window.dispatchEvent(new CustomEvent('app-offline'));
});

const app = createApp(App);

app.use(createPinia())
   .use(router)
   .use(PrimeVue, { ripple: true })
   .use(ToastService)
   .use(ConfirmationService)
   .use(i18n);

// Enregistrer les composants PrimeVue globalement
app.component('PrimeButton', Button);
app.component('PrimeInputText', InputText);
app.component('PrimePassword', Password);
app.component('PrimeCheckbox', Checkbox);
app.component('PrimeCard', Card);
app.component('PrimeSkeleton', Skeleton);
app.component('PrimeAvatar', Avatar);
app.component('PrimeAvatarGroup', AvatarGroup);
app.component('PrimeBadge', Badge);
app.component('PrimeProgressBar', ProgressBar);
app.component('PrimeChart', Chart);
app.component('PrimeDataTable', DataTable);
app.component('PrimeColumn', Column);
app.component('PrimeTag', Tag);
app.component('PrimeDropdown', Dropdown);
app.component('PrimeProgressSpinner', ProgressSpinner);
app.component('PrimeToast', Toast);
app.component('PrimeConfirmDialog', ConfirmDialog);
app.component('PrimeDataView', DataView);
app.component('PrimeMultiSelect', MultiSelect);
app.component('PrimeToggleButton', ToggleButton);
app.component('PrimePaginator', Paginator);
app.component('PrimeDialog', Dialog);
app.component('PrimeTabView', TabView);
app.component('PrimeTabPanel', TabPanel);
app.component('PrimeTextarea', Textarea);
app.component('PrimeCalendar', Calendar);

// Directives
app.directive('tooltip', Tooltip);
   
// Add global property for offline status check
app.config.globalProperties.$isOnline = () => navigator.onLine;

app.mount('#app');